import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Upload, 
  Search, 
  Trash2, 
  Copy, 
  Check, 
  Image as ImageIcon,
  FileImage,
  X,
  Eye,
  Download,
  HardDrive,
  RefreshCw,
  Edit3,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaLibrary = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // État pour le renommage
  const [isRenaming, setIsRenaming] = useState(false);
  const [newFilename, setNewFilename] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  
  // Pagination
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 24;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchFiles = async (searchQuery = '') => {
    try {
      const response = await axios.get(
        `${API}/media/list?skip=${skip}&limit=${limit}&search=${encodeURIComponent(searchQuery)}`,
        getAuthHeaders()
      );
      setFiles(response.data.files || []);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement fichiers:', error);
      toast.error('Erreur lors du chargement des fichiers');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/media/stats/summary`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  useEffect(() => {
    fetchFiles(searchTerm);
    fetchStats();
  }, [skip]);

  const handleSearch = () => {
    setSkip(0);
    fetchFiles(searchTerm);
  };

  const handleUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(
          `${API}/media/upload`,
          formData,
          {
            ...getAuthHeaders(),
            headers: {
              ...getAuthHeaders()?.headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        successCount++;
      } catch (error) {
        console.error(`Erreur upload ${file.name}:`, error);
        errorCount++;
      }
    }

    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} fichier(s) uploadé(s) avec succès`);
      fetchFiles(searchTerm);
      fetchStats();
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} fichier(s) en erreur`);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${file.filename}" ?\n\n⚠️ Cette action est irréversible.\nLes liens utilisant cette image ne fonctionneront plus.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/media/${file.id}`, getAuthHeaders());
      toast.success('Fichier supprimé');
      fetchFiles(searchTerm);
      fetchStats();
      setSelectedFile(null);
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const startRenaming = () => {
    if (selectedFile) {
      // Extraire le nom sans extension
      const filename = selectedFile.filename;
      const lastDot = filename.lastIndexOf('.');
      const nameWithoutExt = lastDot > 0 ? filename.substring(0, lastDot) : filename;
      setNewFilename(nameWithoutExt);
      setIsRenaming(true);
    }
  };

  const cancelRenaming = () => {
    setIsRenaming(false);
    setNewFilename('');
  };

  const handleRename = async () => {
    if (!selectedFile || !newFilename.trim()) return;
    
    setRenameLoading(true);
    try {
      const response = await axios.patch(
        `${API}/media/${selectedFile.id}`,
        { new_filename: newFilename.trim() },
        getAuthHeaders()
      );
      
      toast.success('Fichier renommé avec succès');
      setSelectedFile(response.data);
      setIsRenaming(false);
      setNewFilename('');
      fetchFiles(searchTerm);
    } catch (error) {
      console.error('Erreur renommage:', error);
      toast.error('Erreur lors du renommage');
    } finally {
      setRenameLoading(false);
    }
  };

  const copyUrl = (url) => {
    // Construire l'URL complète
    // L'URL est déjà au format /api/media/file/... depuis le backend
    const fullUrl = `${BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    toast.success('URL copiée dans le presse-papier');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openPreview = (file) => {
    setPreviewImage(file);
    setShowPreview(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F0' }}>
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4" size={40} style={{ color: '#335C2E' }} />
          <p style={{ color: '#333333' }}>Chargement de la bibliothèque...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F0' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#335C2E' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 rounded-full hover:bg-white/10 transition"
              >
                <ArrowLeft size={24} className="text-white" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileImage size={24} />
                  Bibliothèque d'images
                </h1>
                <p className="text-sm" style={{ color: '#C7A027' }}>
                  Hébergement permanent pour vos campagnes email
                </p>
              </div>
            </div>
            
            {/* Upload Button */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml,image/webp"
                multiple
                onChange={handleUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition"
                style={{ background: '#C7A027', color: '#FFFFFF' }}
              >
                {uploading ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  <Upload size={20} />
                )}
                {uploading ? 'Upload en cours...' : 'Téléverser des images'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: '#E8F5E9' }}>
                  <ImageIcon size={24} style={{ color: '#335C2E' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#335C2E' }}>{stats.total_files}</p>
                  <p className="text-sm text-gray-600">Images</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: '#FFF8E1' }}>
                  <HardDrive size={24} style={{ color: '#C7A027' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#C7A027' }}>{stats.total_size_mb} MB</p>
                  <p className="text-sm text-gray-600">Espace utilisé</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: '#E3F2FD' }}>
                  <FileImage size={24} style={{ color: '#1976D2' }} />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Types acceptés</p>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF, SVG, WebP</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>💡 Comment utiliser les images :</strong><br />
            1. Téléversez vos images ici<br />
            2. Cliquez sur "Copier l'URL" pour obtenir l'URL permanente<br />
            3. Collez cette URL dans vos templates d'email pour afficher l'image
          </p>
          <p className="text-xs text-blue-600 mt-2">
            ⚠️ Les URL sont permanentes. Ne supprimez pas une image utilisée dans des emails envoyés.
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg p-4 shadow-sm mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom de fichier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 rounded-lg font-medium"
              style={{ background: '#335C2E', color: '#FFFFFF' }}
            >
              Rechercher
            </button>
          </div>
        </div>

        {/* File Grid */}
        {files.length === 0 ? (
          <div className="bg-white rounded-lg p-12 shadow-sm text-center">
            <ImageIcon size={64} className="mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Aucune image</h3>
            <p className="text-gray-500 mb-4">
              Commencez par téléverser vos premières images
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 rounded-lg font-medium"
              style={{ background: '#335C2E', color: '#FFFFFF' }}
            >
              <Upload size={18} className="inline mr-2" />
              Téléverser des images
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`bg-white rounded-lg shadow-sm overflow-hidden cursor-pointer transition hover:shadow-md ${
                    selectedFile?.id === file.id ? 'ring-2 ring-green-500' : ''
                  }`}
                  onClick={() => setSelectedFile(file)}
                >
                  <div className="aspect-square bg-gray-100 relative">
                    <img
                      src={`${BACKEND_URL}${file.url}`}
                      alt={file.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="absolute inset-0 hidden items-center justify-center bg-gray-200">
                      <ImageIcon size={32} className="text-gray-400" />
                    </div>
                    {/* Quick actions overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreview(file);
                        }}
                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                        title="Voir"
                      >
                        <Eye size={18} className="text-gray-700" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyUrl(file.url);
                        }}
                        className="p-2 bg-white rounded-full hover:bg-gray-100"
                        title="Copier l'URL"
                      >
                        {copiedUrl === file.url ? (
                          <Check size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} className="text-gray-700" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate" title={file.filename}>
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.file_size)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                  className="px-4 py-2 rounded-lg border disabled:opacity-50"
                >
                  Précédent
                </button>
                <span className="text-gray-600">
                  {skip + 1} - {Math.min(skip + limit, total)} sur {total}
                </span>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= total}
                  className="px-4 py-2 rounded-lg border disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}

        {/* Selected File Details */}
        {selectedFile && (
          <div className="fixed inset-y-0 right-0 w-80 bg-white shadow-xl p-6 overflow-y-auto z-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold" style={{ color: '#335C2E' }}>Détails du fichier</h3>
              <button
                onClick={() => setSelectedFile(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="aspect-video bg-gray-100 rounded-lg mb-4 overflow-hidden">
              <img
                src={`${BACKEND_URL}${selectedFile.url}`}
                alt={selectedFile.filename}
                className="w-full h-full object-contain"
              />
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Nom du fichier</label>
                {isRenaming ? (
                  <div className="mt-1">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFilename}
                        onChange={(e) => setNewFilename(e.target.value)}
                        className="flex-1 text-sm p-2 border rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Nouveau nom"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename();
                          if (e.key === 'Escape') cancelRenaming();
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      L'extension sera conservée automatiquement
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={handleRename}
                        disabled={renameLoading || !newFilename.trim()}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded text-white text-sm disabled:opacity-50"
                        style={{ background: '#335C2E' }}
                      >
                        {renameLoading ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <Save size={14} />
                        )}
                        Enregistrer
                      </button>
                      <button
                        onClick={cancelRenaming}
                        className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium break-all flex-1">{selectedFile.filename}</p>
                    <button
                      onClick={startRenaming}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title="Renommer"
                    >
                      <Edit3 size={14} className="text-gray-500" />
                    </button>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Taille</label>
                <p className="text-sm">{formatFileSize(selectedFile.file_size)}</p>
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Type</label>
                <p className="text-sm">{selectedFile.mime_type}</p>
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Date d'upload</label>
                <p className="text-sm">{formatDate(selectedFile.uploaded_at)}</p>
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Uploadé par</label>
                <p className="text-sm">{selectedFile.uploaded_by || 'Inconnu'}</p>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">URL permanente</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`${BACKEND_URL}${selectedFile.url}`}
                    readOnly
                    className="flex-1 text-xs p-2 border rounded bg-gray-50 font-mono"
                  />
                  <button
                    onClick={() => copyUrl(selectedFile.url)}
                    className="px-3 py-2 rounded text-white"
                    style={{ background: '#335C2E' }}
                    title="Copier l'URL"
                  >
                    {copiedUrl === selectedFile.url ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </div>

              <div className="pt-4 flex gap-2">
                <button
                  onClick={() => openPreview(selectedFile)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
                >
                  <Eye size={16} />
                  Voir
                </button>
                <a
                  href={`${BACKEND_URL}${selectedFile.url}`}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
                >
                  <Download size={16} />
                  Télécharger
                </a>
              </div>

              <button
                onClick={() => handleDelete(selectedFile)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-red-600 border border-red-200 hover:bg-red-50"
              >
                <Trash2 size={16} />
                Supprimer
              </button>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && previewImage && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <div className="relative max-w-4xl max-h-[90vh]">
              <button
                onClick={() => setShowPreview(false)}
                className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
              >
                <X size={24} />
              </button>
              <img
                src={`${BACKEND_URL}${previewImage.url}`}
                alt={previewImage.filename}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <p className="text-center text-white mt-2 text-sm">
                {previewImage.filename}
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MediaLibrary;
